import { DomainReference, Optional } from '@rygen/rygen-ui/model';

import { RateQuoteGroupType } from '../rateShop/RateQuoteGroupType';

import { RouteBuilderPhase2Step } from './RouteBuilderPhase2Step';
import { RoutePlanProposedShipment } from './RoutePlanProposedShipment';
import { RoutePlanSelectRateGroupPayload } from './RoutePlanSelectRateGroupPayload';
import { RoutePlanShipmentRequest } from './RoutePlanShipmentRequest';
import { RoutePlanStatus } from './RoutePlanStatus';
import { RoutePlanStop } from './RoutePlanStop';

export class RouteBuilderPhase2Request {
  shipmentRequests: RoutePlanShipmentRequest[] = [];
  skipAutoAction = false;

  /**
   * Id of the persisted route plan, once a draft has been saved (or the plan
   * was reopened from the list). Serialized as `routePlanId`, so committing
   * (`createRoutePlan`) attaches to this draft instead of creating a fresh one.
   */
  routePlanId?: number;
  /** Persisted status, when reopened from the list. Used to gate read-only mode. */
  status?: RoutePlanStatus;
  /**
   * Live count of committed shipments on the reopened plan. A DRAFT that already
   * has committed shipments is read-only even before it freezes (freeze happens
   * later, on first pickup) — re-editing or re-committing would fork from
   * shipments that already exist.
   */
  shipmentCount?: number;

  _currentStep: RouteBuilderPhase2Step = 'planning';
  _rateQuoteGroupType: RateQuoteGroupType = 'none';
  _owner?: DomainReference;

  constructor(opts?: Partial<RouteBuilderPhase2Request>) {
    if (opts?.shipmentRequests != null) {
      this.shipmentRequests = opts.shipmentRequests.map(
        (s) => new RoutePlanShipmentRequest(s),
      );
    }

    if (opts?.owner ?? opts?._owner) {
      this._owner = new DomainReference(opts.owner ?? opts._owner);
    }

    if (opts?.skipAutoAction != null) {
      this.skipAutoAction = opts.skipAutoAction;
    }

    if (opts?.routePlanId != null) {
      this.routePlanId = opts.routePlanId;
    }

    if (opts?.status != null) {
      this.status = opts.status;
    }

    if (opts?.shipmentCount != null) {
      this.shipmentCount = opts.shipmentCount;
    }
  }

  get isCommitted(): boolean {
    // Read-only once the plan has materialized shipments: it is EXECUTED, or it was
    // reopened with committed shipments already attached (shipmentCount > 0, even
    // while still DRAFT — the plan freezes to EXECUTED later, on first pickup).
    return (
      this.status === RoutePlanStatus.EXECUTED || (this.shipmentCount ?? 0) > 0
    );
  }

  get proposedShipments(): RoutePlanProposedShipment[] {
    return this.shipmentRequests.map((r) => r.proposedShipment);
  }

  get currentStep(): RouteBuilderPhase2Step {
    return this._currentStep;
  }

  get rateQuoteGroupType(): RateQuoteGroupType {
    return this._rateQuoteGroupType;
  }

  get owner(): Optional<DomainReference> {
    return this._owner;
  }

  set owner(owner: DomainReference) {
    this._owner = owner;
    this.shipmentRequests.forEach((s) => {
      s.proposedShipment.owner = owner;
    });
  }

  // The getters below aren't intended for real usage. They're here to make
  // this class match the shape of other `ParentEntity` types.
  get id() {
    return undefined;
  }

  get origin() {
    return undefined;
  }

  get destination() {
    return undefined;
  }

  get stops() {
    return this.shipmentRequests.map((s) => s.proposedShipment.stops).flat();
  }

  get requestedAccessorials() {
    return [];
  }
  // End pointless getters

  reset() {
    this.shipmentRequests = [];
    this._currentStep = 'planning';
    // Detach from any reopened plan; clearProposedShipments() deliberately keeps
    // these so Clear stays on the same plan.
    this.routePlanId = undefined;
    this.status = undefined;
    this.shipmentCount = undefined;
  }

  setValidationError() {
    this._currentStep = 'validation_error';
  }

  moveToRating(isValid = false) {
    if (this._currentStep !== 'planning') {
      return;
    }

    this._currentStep = isValid ? 'rating' : 'validation_error';
  }

  backToPlanning() {
    this._currentStep = 'planning';
    this._rateQuoteGroupType = 'none';
  }

  moveToExecuting(payload?: RoutePlanSelectRateGroupPayload) {
    this._currentStep = 'executing';

    if (payload) {
      const { type, rates, rateSelectionReason } = payload;
      this._rateQuoteGroupType = type;
      this.shipmentRequests = rates;

      if (rateSelectionReason) {
        this.shipmentRequests.forEach((s) => {
          s.rateSelectionReason = rateSelectionReason;
        });
      }
    }
  }

  backToRating() {
    if (this._currentStep !== 'executing') {
      return;
    }

    this._currentStep = 'rating';
    this._rateQuoteGroupType = 'none';
    this.shipmentRequests.forEach((s) => {
      s.rateQuote = undefined;
      s.rateSelectionReason = undefined;
    });
  }

  nextIdentifier() {
    const identifiers = this.shipmentRequests.map((s) => s.shipmentIdentifier);
    return Math.max(...identifiers, 0) + 1;
  }

  findProposedShipment(
    shipmentIdentifier: RoutePlanProposedShipment['shipmentIdentifier'],
  ): {
    index: number;
    proposedShipment: RoutePlanProposedShipment;
  } {
    const index = this.shipmentRequests.findIndex(
      (s) => s.shipmentIdentifier === shipmentIdentifier,
    );
    const shipmentRequest = this.shipmentRequests[index];
    if (!shipmentRequest) {
      throw new Error(`Error finding proposed shipment ${shipmentIdentifier}`);
    }

    return {
      index,
      proposedShipment: shipmentRequest.proposedShipment,
    };
  }

  /**
   * Creates and insert a `RoutePlanShipmentRequest` for a `RoutePlanProposedShipment`
   *
   * @param proposedShipment the `RoutePlanProposedShipment` to insert
   * @returns the index of the newly inserted shipment
   */
  insertProposedShipment(proposedShipment: RoutePlanProposedShipment) {
    proposedShipment.shipmentIdentifier = this.nextIdentifier();

    const request = new RoutePlanShipmentRequest({ proposedShipment });
    this.shipmentRequests.push(request);
    return this.shipmentRequests.length - 1;
  }

  /**
   * Add a new proposed shipment to the route plan
   *
   * @param stops the stops for the new proposed shipment
   * @returns the index of the new proposed shipment
   */
  addProposedShipment(stops: RoutePlanStop[]): {
    index: number;
    identifier: RoutePlanProposedShipment['identifier'];
  } {
    const proposedShipment = new RoutePlanProposedShipment({
      shipmentIdentifier: this.nextIdentifier(),
      owner: this.owner,
      stops,
    });

    const shipmentRequest = new RoutePlanShipmentRequest({
      proposedShipment,
    });
    this.shipmentRequests.push(shipmentRequest);

    return {
      index: this.shipmentRequests.length - 1,
      identifier: shipmentRequest.shipmentIdentifier,
    };
  }

  clearProposedShipments() {
    this.shipmentRequests = [];
  }

  removeProposedShipment(
    shipmentIdentifier: RoutePlanProposedShipment['shipmentIdentifier'],
  ) {
    this.shipmentRequests = this.shipmentRequests.filter(
      (r) => r.shipmentIdentifier !== shipmentIdentifier,
    );
  }
}
